import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Workspace } from '../types/Metadata';
import { View } from '../types/View';
import { AuthClient } from './authClient';

const RETRY_PARAM_NAME = 'r';
const MAX_RETRY = 3;

type SearchParams = {
  v?: string;
  id?: string;
  c?: string;
  m?: string;
  cid?: string;
};

namespace URLManager {
  export type Params = {
    itemRefId?: string;
    w?: string;
    path?: string;
  } & SearchParams;
  export type UpdateURLFn = (p: Params) => void;
}

class URLManager {
  public static isAuthCallbackOnly() {
    const params = new URLSearchParams(window.location.search);
    let cbOnly = true;
    params.forEach((v, k) => {
      if (k != RETRY_PARAM_NAME) cbOnly = false;
    });
    return cbOnly;
  }
  public static reload() {
    const params = new URLSearchParams(window.location.search);
    const r = parseInt(params.get(RETRY_PARAM_NAME) || '0');
    if (r < MAX_RETRY) {
      // Prevent page being refresh infinitely
      params.set(RETRY_PARAM_NAME, `${r + 1}`);
      setTimeout(() => {
        AuthClient.clear();
        window.location.search = params.toString();
      }, r * 500);
    }
  }

  public static useRouter() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const navigate = useNavigate();
    const wid = params['wid'];
    const vid = searchParams.get('v');
    const c = searchParams.get('c');
    const id = searchParams.get('id');
    const m = searchParams.get('m');
    const existingParams = [...searchParams.entries()].reduce((r, [k, v]) => {
      r[k] = v;
      return r;
    }, {} as any);
    return {
      wid,
      vid,
      id,
      c,
      m,
      filters: parseJSON(searchParams.get('f')),
      sortBys: parseJSON(searchParams.get('s')),
      updateSearchParams: (p: SearchParams) => {
        setSearchParams({ ...existingParams, ...p });
      },
      updateUrl: ({ w, path, itemRefId, ...p }: URLManager.Params) => {
        if (itemRefId || (w && (wid != w || !!path))) {
          const { c, v, id, m } = p;
          navigate(this.generateUrl(w, { path, itemRefId, c, v, id, m }));
        } else {
          if (!p.c) delete existingParams.c;
          if (!p.m) delete existingParams.m;
          if (!p.id || p.id === null) {
            delete existingParams.id;
            delete p.id;
          }
          setSearchParams({ ...existingParams, ...p });
        }
      }
    };
  }
  public static generateUrl(
    wid: string | Workspace,
    params: {
      v?: string | View.Setting;
      id?: string;
      itemRefId?: string;
      path?: string;
      cid?: string;
      c?: string;
      m?: string;
    } = {}
  ) {
    if (!wid) {
      return;
    }
    if (params.itemRefId) {
      const queryParams = ['c', 'v', 'id', 'cid', 'm'].reduce((acc, key) => {
        if (params[key]) {
          acc.push(`${key}=${encodeURIComponent(params[key])}`);
        }
        return acc;
      }, []);

      const queryString =
        queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      return `/item/${params.itemRefId}${queryString}`;
    }
    if (typeof wid != 'string') {
      let w = wid;
      if (params.v && typeof params.v != 'string') {
        params.v = params.v.props?.refId;
      }
      if (!params.v && !params.path) {
        const view = w.folders?.reduce((views, folder) => {
          return [...views, ...folder.views];
        }, w.views || [])[0];
        if (view) params.v = view.props.refId;
      }
      wid = w.props.refId;
    }
    const queries = Object.entries(params)
      .filter(([k, v]) => v && k != 'path')
      .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
      .join('&');
    return [`/w/${wid}${params?.path || ''}`, queries].filter(x => x).join('?');
  }
  public static stringify(json: View.FiltersField[] | View.SortByField[]) {
    if (!json?.length) return '';
    return JSON.stringify(minify(json));
  }
}

const MinifyMapping = {
  field: 'f',
  conditions: 'c',
  order: 'o',
  o: 'order',
  f: 'field',
  c: 'conditions'
};
const minify = (json: any) => {
  if (!json) return json;
  if (Array.isArray(json)) {
    return json.map(minify);
  }
  if (typeof json == 'object') {
    return Object.entries(json).reduce((obj, [k, v]) => {
      if (['isRequired', 'uuid'].includes(k)) return obj; // exclude isRequired/uuid from url
      const m = MinifyMapping[k] || k;
      obj[m] = v;
      return obj;
    }, {});
  }
  return json;
};

const parseJSON = (s: string) => {
  if (!s) return null;
  try {
    return minify(JSON.parse(s));
  } catch (err) {
    console.warn('Failed to parse', s, err);
  }
};

export default URLManager;
